/**
 * Search feedback for list pages: the result count and which fields the typed text matched on.
 * Rows carry `matched_on` (field labels) from the API whenever a search term was sent.
 */

/** Adds a "Matched on" column to a table's columns while a search is active. */
export function withMatchColumn(columns, search) {
  if (!search?.trim()) return columns;
  return [
    ...columns,
    {
      key: 'matched_on',
      label: 'Matched on',
      render: (row) => <MatchBadges fields={row.matched_on} />,
    },
  ];
}

export function MatchBadges({ fields }) {
  if (!fields?.length) return <span className="text-xs text-gray-400">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {fields.map((f) => (
        <span key={f} className="rounded-full bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary">
          {f}
        </span>
      ))}
    </div>
  );
}

/**
 * @param search   the debounced term actually sent to the API
 * @param total    total matches (pass the pagination total when the list is paged)
 * @param rows     rows currently loaded, used for the per-field breakdown
 * @param paged    true when `rows` is only one page of `total`
 * @param plainCount  text shown when no search is active (omit to render nothing)
 */
export default function SearchSummary({ search, total, rows = [], paged = false, plainCount }) {
  const term = search?.trim();
  if (!term) {
    return plainCount ? <div className="mb-2 text-sm text-gray-500">{plainCount}</div> : null;
  }

  const counts = {};
  rows.forEach((r) => (r.matched_on || []).forEach((f) => (counts[f] = (counts[f] || 0) + 1)));
  const entries = Object.entries(counts);

  return (
    <div className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-gray-600">
      <span>
        <span className="font-semibold text-gray-900">{total ?? rows.length}</span> {(total ?? rows.length) === 1 ? 'result' : 'results'} for “{term}”
      </span>
      {entries.length > 0 && (
        <>
          <span className="text-gray-300">·</span>
          <span className="text-xs text-gray-500">Matched on{paged ? ' (this page)' : ''}:</span>
          {entries.map(([field, n]) => (
            <span key={field} className="rounded-full bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary">
              {field} · {n}
            </span>
          ))}
        </>
      )}
    </div>
  );
}
